// =====================================================================
// CommandesPretesChargement.jsx — Commandes prêtes v2 (16-10-2026) — CHARGEMENT
// =====================================================================
// Route /commande-prete/chargement (SPEC-v2 §1.5, §3 « Chargement », §5). Droit
// « Charger un camion » (commandes_pretes.load.own, ou l'admin du module — SPEC v3 D, 17/10).
//   v3 (décision Silviu 16/10) : PLUS de choix du client — il vient de l'encodage.
//   1. camion / transporteur (facultatif) → « Commencer » (POST /loadings : un départ
//   ouvert par utilisateur, rouvert s'il existe ; il peut porter plusieurs clients) ;
//   2. champ de scan TOUJOURS focalisé : Entrée = scan de la RÉF. DE CHARGEMENT (BROUT…)
//   → sa commande s'ouvre dans le départ (même réf. chez plusieurs clients, ou en test
//   et en réel → choix sur la page ; la commande de test ne se charge jamais), ou de
//   l'étiquette (SSCC) de la palette / du colis qui monte dans le camion → chargée, sa
//   commande trouvée sans client préalable. Les scans partent l'un après l'autre (file
//   d'attente) : le lecteur n'attend jamais le serveur, le champ n'est jamais grisé ;
//   3. par commande (avec son client) : unités « à quai » / « chargée », « Tout charger » ;
//      « Annuler le dernier scan » ; « Valider le départ » → récapitulatif et
//      confirmation SUR LA PAGE (jamais window.confirm) ; mail « chargée » PAR client.
// Erreurs de scan (inconnue, déjà chargée, commande de test…) en GROS, sans quitter
// le champ, et elles RESTENT affichées (une par scan raté) jusqu'à « Vu » : le scan
// suivant réussi ne les efface pas. Les boutons ne prennent pas le focus (mousedown
// sans effet) : l'Entrée du scan suivant ne peut jamais rappuyer sur « Tout charger »
// ni valider le départ. Pendant la validation du départ, les scans sont refusés (le
// code reste dans le champ) : rien ne se charge dans un départ en train de se fermer.
// Logique PURE : pages/commande-prete/chargement.js (testée).
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { readyLoadings, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import BadgeTest from './commande-prete/BadgeTest.jsx';
import { abregeSscc } from './commande-prete/sscc.js';
import { heureBxl, dateHeureBxl } from './commande-prete/dates.js';
import {
  extraireChargement, avertissementsReponse, etatUnite, resumeCommande, resumeDepart, dernierLot, texteLot,
  departFerme, peutAnnuler, peutValider, peutToutCharger, texteRecapDepart, verifCodeScan,
  issueScanSansReponse, etatMailCommande, textesMailsDepart, texteUnites, texteScan, texteAnnule,
  texteOuvert, lireChoix, libelleChoix, clientDe, MSG_AUCUNE_COMMANDE, mailIncertain, texteDepartReserve,
  texteAQuai, confirmationTout, confirmationValable, confirmationRefaite, texteConfirmerTout, texteToutCharge, refDuCamion,
  chargeAutreJour,
} from './commande-prete/chargement.js';
import { droitsCommandesPretes, texteAccesRefuse, DROIT_CHARGER, DROIT_LIRE } from './commande-prete/droits.js';

const MAX_CAMION = 60;   // comme le serveur (« Camion : 60 caractères au plus »)

const heure = heureBxl;
const dateHeure = (d) => dateHeureBxl(d);
// Un bouton touché ne prend pas le focus : il reste dans le champ de scan.
const garderFocus = (e) => e.preventDefault();
const nomUnite = (t) => `${t.unite.nom} · ${t.commande.orderRef}`;

export default function CommandesPretesChargement() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const droits = droitsCommandesPretes(can);
  const autorise = droits.charger;

  const [camion, setCamion] = useState('');
  const [choix, setChoix] = useState(null);        // v3 : réf. ambiguë → { lid, items } (commandes proposées)
  const scanSeq = useRef(0);                        // numéro du dernier scan mis en file (propositions périmées)
  const codesScans = useRef(new Map());             // n° de scan → code (une double lecture de la même réf.)
  const [ch, setCh] = useState(null);              // LoadingDTO du départ ouvert
  const [ouverture, setOuverture] = useState(false);
  const ouvertureRef = useRef(false);               // (lecteur : deux « Entrée » de suite → une seule ouverture)
  const [scan, setScan] = useState('');
  const [message, setMessage] = useState(null);    // dernier résultat : { type: ok | warn, txt, detail?: [] }
  const [alertes, setAlertes] = useState([]);      // erreurs NON vues (les plus récentes d'abord), jusqu'à « Vu »
  const [fermeture, setFermeture] = useState(false);   // « Confirmer le départ » en cours
  const fermetureRef = useRef(false);
  const refusesFermeture = useRef([]);   // codes scannés pendant la validation (NON chargés)
  const alerteSeq = useRef(0);
  const [recap, setRecap] = useState(false);
  const [aConfirmer, setAConfirmer] = useState(null);   // « Tout charger » qui attend « Oui » / « Non » : confirmationTout()
  const confEnvoyee = useRef(null);                     // « Oui » déjà parti pour CETTE confirmation (double toucher : un envoi)
  const [toutEnCours, setToutEnCours] = useState(() => new Set());   // « départ:commande » dont « Tout charger » attend la réponse
  const [enCours, setEnCours] = useState(0);

  const chRef = useRef(null);
  const file = useRef(Promise.resolve());
  const champScan = useRef(null);
  const champCamion = useRef(null);
  const racine = useRef(null);

  const nomsDeparts = useRef(new Map());   // départ → camion (pour nommer une erreur d'un autre départ)
  const poser = (dto) => {
    chRef.current = dto;
    if (dto && dto.id) nomsDeparts.current.set(dto.id, dto.truck || '');
    setCh(dto);
  };
  // Réponse d'une requête partie pour le départ `lid` : appliquée seulement si l'écran le montre
  // encore (« Nouveau départ » entre-temps : on ne revient pas en arrière tout seul).
  const poserSi = (lid, dto) => { if (dto && chRef.current && chRef.current.id === lid) poser(dto); };
  const ouvert = !!ch && !departFerme(ch);
  // « Tout charger » en attente de « Oui » : si la commande change, la phrase est refaite avec les unités de MAINTENANT et
  // signalée dans le cadre (le message du dernier geste reste) ; si elle ne se charge plus ici, le cadre disparaît.
  // « Oui » ne part jamais sur une phrase qui n'est plus vraie.
  useEffect(() => {
    if (aConfirmer && !confirmationValable(aConfirmer, ch)) setAConfirmer(confirmationRefaite(aConfirmer, ch));
  }, [ch, aConfirmer]);

  // ---------- Focus : le champ de scan, toujours (sauf pendant la saisie d'un autre champ) ----------
  const focusScan = () => {
    const el = champScan.current;
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!ouvert) return;
    const a = document.activeElement;
    if (!a || a === document.body || (a.tagName === 'BUTTON' && racine.current && racine.current.contains(a))) focusScan();
  });
  // Une touche tapée hors d'un champ (focus perdu après un toucher ailleurs) part dans le champ de scan.
  useEffect(() => {
    if (!ouvert) return undefined;
    const onKey = (e) => {
      const el = champScan.current;
      if (!el || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const a = document.activeElement;
      if (a === el) return;
      if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) return;
      if (e.key === ' ' && a && (a.tagName === 'BUTTON' || a.tagName === 'A' || a.tagName === 'SUMMARY')) return;   // espace = activer le bouton (clavier)
      el.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [ouvert]);

  // ---------- File d'attente : une requête à la fois, dans l'ordre des scans ----------
  const enCoursRef = useRef(0);
  const enfiler = (tache) => {
    enCoursRef.current += 1;
    setEnCours((n) => n + 1);
    const p = file.current
      .then(() => tache())
      .catch((e) => { console.error('[chargement]', e); })
      .finally(() => { enCoursRef.current -= 1; setEnCours((n) => n - 1); });
    file.current = p;
    return p;
  };
  // Une erreur s'ajoute aux alertes (jamais écrasée par le résultat suivant) ; « Vu » les retire.
  const erreur = (txt) => {
    alerteSeq.current += 1;
    const a = { id: alerteSeq.current, txt };
    setAlertes((l) => [a, ...l]);   // jamais tronquée : chaque scan raté reste jusqu'à « Vu »
    try { if (navigator.vibrate) navigator.vibrate(250); } catch { /* sans vibreur */ }
  };
  const toutVu = () => { setAlertes([]); focusScan(); };
  // Réponse d'une requête partie pour le départ `lid` : son message ne s'affiche que si ce départ
  // est encore à l'écran (sinon il s'afficherait sur un autre départ).
  const ici = (lid) => !!chRef.current && chRef.current.id === lid;
  const dire = (lid, m) => { if (ici(lid)) setMessage(m); };
  // Une erreur n'est JAMAIS perdue : celle d'un départ qui n'est plus à l'écran s'affiche quand même, nommée.
  const crier = (lid, txt) => erreur(ici(lid) ? txt : `Départ ${nomsDeparts.current.get(lid) || 'précédent'} : ${txt}`);
  // Réponse d'erreur { error, message, dto } : le départ joint remplace l'écran, le message s'affiche.
  const surErreur = (lid, err, prefixe, code) => {
    const d = err?.response?.data || {};
    poserSi(lid, extraireChargement(d));
    if (!err?.response) return false;   // pas de réponse : à l'appelant de relire le départ
    const m = d.message || errMsg(err);
    crier(lid, `${prefixe ? `${prefixe} : ` : ''}${m}${code ? ` — ${abregeSscc(code)}` : ''}`);
    return true;
  };
  const relire = async (lid) => {
    try { const dto = extraireChargement(await readyLoadings.get(lid)); poserSi(lid, dto); return dto; } catch { return null; }
  };

  // ---------- 1. Camion, ouverture du départ (v3 : sans client) ----------
  const commencer = async () => {
    if (ouvertureRef.current) return;
    ouvertureRef.current = true;
    const truck = camion.trim().replace(/\s+/g, ' ').slice(0, MAX_CAMION);   // comme le serveur (espaces regroupés)
    // (29-10) Réf. BROUT… scannée dans « Camion » (habitude de Vincent) : sa commande s'ouvre aussi, comme scannée.
    const ref = refDuCamion(truck);
    setOuverture(true);
    setMessage(null);
    try {
      const r = await readyLoadings.open({ truck });
      const dto = extraireChargement(r);
      if (!dto || !dto.id) throw new Error('Réponse inattendue du serveur');
      poser(dto);
      setRecap(false);
      setChoix(null);
      const deja = resumeDepart(dto).unitesIci;
      let repris = null;
      if (r.existing) {
        const n = Number(r.refsRetirees) || 0;
        const retirees = n ? ` ${n} réf. ouverte${n > 1 ? 's' : ''} un autre jour (rien de chargé) retirée${n > 1 ? 's' : ''}.` : '';
        repris = `Départ déjà ouvert : repris${deja ? ` (${texteUnites(deja)} déjà chargée${deja > 1 ? 's' : ''})` : ''}.${retirees}`;
        setMessage({ type: 'warn', txt: repris });
      }
      if (ref) envoyerCode(dto.id, ref, { prefixe: 'Réf. du champ Camion NON ouverte', note: repris });
    } catch (e) {
      const d = e?.response?.data || {};
      const ancien = d.error === 'DEPART_OUVERT' ? extraireChargement(d) : null;
      if (ancien && ancien.id) {
        // Départ resté ouvert avec des unités chargées (autre jour, autre camion) : il s'ouvre ici,
        // pour être validé ou ses scans annulés — sinon plus rien ne pourrait être chargé.
        poser(ancien);
        setRecap(false);
        setMessage({
          type: 'warn', txt: `${d.message || errMsg(e)} — le voici : validez-le, ou annulez ses scans.`,
          ...(ref ? { detail: [chargeAutreJour(ancien)
            ? `Réf. ${ref} (champ Camion) NON ouverte : ce départ a des unités chargées un autre jour — validez-le d'abord, puis recommencez avec elle.`
            : `Réf. ${ref} (champ Camion) NON ouverte : si elle part dans ce camion, scannez-la ci-dessous ; sinon validez d'abord ce départ, puis recommencez avec elle.`] } : {}),
        });
      } else erreur(`${errMsg(e)}${ref ? ` (réf. ${ref} NON ouverte)` : ''}`);
    } finally { ouvertureRef.current = false; setOuverture(false); }
  };
  const nouveauDepart = () => {
    if (enCoursRef.current > 0) return;   // requêtes en route : leur résultat s'affiche ici d'abord
    poser(null);
    setRecap(false);
    setChoix(null);
    setMessage(null);
    setCamion('');
    setTimeout(() => champCamion.current?.focus(), 0);
  };

  // ---------- 3. Scan ----------
  const onEnterScan = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const brut = scan;
    if (fermetureRef.current) {
      const code = brut.trim();
      if (code) {
        refusesFermeture.current.push(abregeSscc(code));
        setScan('');
        erreur(`Validation du départ en cours : ${abregeSscc(code)} n'est PAS chargé. S'il part dans ce camion, rescannez-le après la validation (dans « Nouveau départ » si le départ est validé).`);
      }
      return;
    }
    setScan('');
    if (!chRef.current || departFerme(chRef.current)) return;
    const v = verifCodeScan(brut);
    if (v.erreur) { if (brut.trim()) erreur(v.erreur); return; }   // Entrée seule : rien
    envoyerCode(chRef.current.id, v.code);   // le départ À L'ÉCRAN au moment du scan
  };
  // Code envoyé au départ `lid` — scanné, ou réf. du champ « Camion » au « Commencer » : une étiquette se charge, une
  // réf. ouvre sa commande. `prefixe` nomme l'origine dans une erreur ; `note` reste en tête du détail du résultat.
  const envoyerCode = (lid, code, { prefixe = null, note = null } = {}) => {
    // Un nouveau scan : les propositions d'une réf. précédente (et leur message) ne valent plus.
    setChoix(null);
    setMessage((m) => (m && m.choix ? null : m));
    const numeroScan = ++scanSeq.current;
    codesScans.current.set(numeroScan, code);
    codesScans.current.delete(numeroScan - 50);   // (mémoire bornée)
    setRecap(false);   // la liste change : le récapitulatif sera relu
    const resultat = (m) => dire(lid, note ? { ...m, type: 'warn', detail: [note, ...(m.detail || [])] } : m);
    enfiler(async () => {
      const avant = ici(lid) ? chRef.current : null;   // état juste avant ce scan (scans précédents compris)
      try {
        const r = await readyLoadings.scan(lid, code);
        poserSi(lid, extraireChargement(r));
        const av = avertissementsReponse(r);
        const proposees = lireChoix(r);
        // (un scan saisi depuis : ses propositions ne s'affichent plus — elles ne valent que pour CE scan)
        const dernierScan = numeroScan === scanSeq.current;
        if (proposees.length) {   // v3 : réf. ambiguë — rien d'ouvert, l'opérateur choisit sur la page
          // (alerte qui RESTE jusqu'à « Vu » : le résultat du scan suivant ne doit pas l'effacer — sauf double
          // lecture de la même réf. : le scan suivant affiche lui-même le choix)
          if (!dernierScan) { if (codesScans.current.get(numeroScan + 1) !== code) crier(lid, `Réf. ${code} NON ouverte (plusieurs commandes portent cette réf.) : un autre scan a suivi — rescannez-la pour choisir la commande.`); }
          else {
            if (ici(lid)) setChoix({ lid, items: proposees });
            resultat({ type: 'warn', txt: av[0] || `Réf. ${code} : choisissez la commande`, detail: av.slice(1), choix: true });
          }
        } else if (r && r.ouvert) {
          resultat({ type: av.length ? 'warn' : 'ok', txt: `✓ ${texteOuvert(r)}`, detail: av });
        } else resultat({ type: av.length ? 'warn' : 'ok', txt: `✓ ${texteScan(r) || abregeSscc(code)} chargée`, detail: av });
      } catch (err) {
        if (surErreur(lid, err, prefixe, code)) return;
        // Pas de réponse : le scan est peut-être passé — le départ relu, comparé à AVANT, le dit.
        const relu = await relire(lid);
        const issue = issueScanSansReponse(avant, relu, code);
        if (issue.ouvert) resultat({ type: 'ok', txt: `✓ ${issue.ouvert.orderRef} (${clientDe(issue.ouvert)}) ouverte.` });
        else if (issue.charge) resultat({ type: 'ok', txt: `✓ ${nomUnite(issue.charge)} chargée` });
        else if (issue.deja) crier(lid, `${nomUnite(issue.deja)} était DÉJÀ chargée dans ce départ (scannée deux fois ?) — ${abregeSscc(code)}`);
        else crier(lid, `${prefixe ? `${prefixe} : ` : ''}Scan NON enregistré (pas de réponse du serveur) : rescannez ${abregeSscc(code)}.`);
      }
    });
  };

  // v3 : commande choisie dans la liste proposée (réf. ambiguë)
  const ouvrirChoisie = (c) => {
    focusScan();
    if (!choix || fermetureRef.current || !c.chargeable) return;
    const lid = choix.lid;
    setChoix(null);
    setMessage((m) => (m && m.choix ? null : m));
    enfiler(async () => {
      try {
        const r = await readyLoadings.openOrder(lid, c.orderId);
        poserSi(lid, extraireChargement(r));
        const av = avertissementsReponse(r);
        dire(lid, { type: av.length ? 'warn' : 'ok', txt: `✓ ${texteOuvert(r) || `${c.orderRef} ouverte.`}`, detail: av });
      } catch (err) {
        if (surErreur(lid, err, `${c.orderRef} non ouverte`)) return;
        await relire(lid);
        crier(lid, `${c.orderRef} : pas de réponse du serveur — vérifiez la liste ci-dessous.`);
      }
    });
  };

  // ---------- 4. Tout charger, annuler, valider ----------
  // « Tout charger » (29-10-2026, demande Silviu) : 1er toucher = la phrase « Je confirme avoir chargé 5 palettes… à mon nom »,
  // figée ; « Oui » envoie ces unités-là — le serveur refuse sans confirmation, ou si une autre unité est à quai.
  const demanderToutCharger = (c) => {
    focusScan();
    if (fermetureRef.current || !peutToutCharger(chRef.current, c)) return;
    setRecap(false);
    setAConfirmer(confirmationTout(c, chRef.current.id));
  };
  const toutCharger = (c, conf) => {
    focusScan();
    if (confEnvoyee.current === conf) return;
    confEnvoyee.current = conf;
    setAConfirmer(null);
    const ch0 = chRef.current;
    // confirmé, mais plus envoyable (validation du départ, liste changée entre-temps) : dit, jamais muet
    const motif = fermetureRef.current ? 'validation du départ en cours'
      : !confirmationValable(conf, ch0) ? 'la liste vient de changer — vérifiez-la, puis recommencez' : null;
    if (motif) { if (ch0) crier(ch0.id, `« Tout charger » ${c.orderRef} non fait : ${motif}.`); return; }
    setRecap(false);
    const lid = ch0.id;
    const cle = `${lid}:${c.id}`;
    setToutEnCours((s) => new Set(s).add(cle));
    enfiler(async () => {
      try {
        const r = await readyLoadings.loadOrder(lid, c.id, conf.ids);
        poserSi(lid, extraireChargement(r));
        const av = avertissementsReponse(r);
        dire(lid, { type: av.length ? 'warn' : 'ok', txt: texteToutCharge(c, conf, r && r.charge), detail: av });
      } catch (err) {
        if (surErreur(lid, err, `« Tout charger » ${c.orderRef} non fait`)) return;
        await relire(lid);
        crier(lid, `« Tout charger » ${c.orderRef} : pas de réponse du serveur — vérifiez la liste ci-dessous.`);
      } finally { setToutEnCours((s) => { const x = new Set(s); x.delete(cle); return x; }); }
    });
  };
  const annuler = () => {
    focusScan();
    if (fermetureRef.current || !peutAnnuler(chRef.current)) return;
    setRecap(false);
    const lid = chRef.current.id;
    // Le geste MONTRÉ comme le dernier : si un scan en route le dépasse, le serveur n'annule rien.
    const lot = dernierLot(chRef.current);
    const loadedAt = lot && lot.unites[0].unite.loadedAt;
    enfiler(async () => {
      try {
        const r = await readyLoadings.undo(lid, loadedAt);
        poserSi(lid, extraireChargement(r));
        const t = texteAnnule(r);
        dire(lid, { type: 'ok', txt: t ? `↶ Remis à quai : ${t}` : '↶ Dernier scan annulé.' });
      } catch (err) {
        if (surErreur(lid, err, 'Annulation impossible')) return;
        await relire(lid);
        crier(lid, 'Annulation : pas de réponse du serveur — vérifiez la liste ci-dessous.');
      }
    });
  };
  const ouvrirRecap = () => {
    focusScan();
    const v = peutValider(chRef.current);
    if (!v.ok) { if (v.msg) setMessage({ type: 'warn', txt: v.msg }); return; }
    setMessage(null);
    setAConfirmer(null);
    setRecap(true);
  };
  const confirmerDepart = () => {
    focusScan();
    if (!recap || enCoursRef.current > 0 || !chRef.current || fermetureRef.current) return;
    const lid = chRef.current.id;
    fermetureRef.current = true;
    refusesFermeture.current = [];
    setFermeture(true);
    enfiler(async () => {
      try {
        const r = await readyLoadings.close(lid);
        poserSi(lid, extraireChargement(r));
        setRecap(false);
        // v3 : facturation automatique (palettes sorties) non faite → avertissements, le départ reste validé
        const av = avertissementsReponse(r);
        dire(lid, { type: av.length ? 'warn' : 'ok', txt: '✓ Départ validé.', detail: av });
      } catch (err) {
        setRecap(false);
        if (surErreur(lid, err, 'Départ non validé')) return;
        const relu = await relire(lid);
        if (relu && departFerme(relu)) dire(lid, { type: 'ok', txt: '✓ Départ validé.' });
        else crier(lid, 'Départ non validé (pas de réponse du serveur) : réessayez.');
      } finally {
        fermetureRef.current = false;
        setFermeture(false);
        // Scannés pendant la validation : rappelés une fois le résultat connu (jamais oubliés).
        const refuses = refusesFermeture.current;
        refusesFermeture.current = [];
        if (refuses.length) {
          erreur(departFerme(chRef.current)
            ? `Scannés pendant la validation, NON chargés : ${refuses.join(', ')} — s'ils partent, ouvrez « Nouveau départ » et rescannez-les.`
            : `Scannés pendant la validation, NON chargés : ${refuses.join(', ')} — rescannez-les.`);
        }
      }
    });
  };

  // =====================================================================
  // Rendu
  // =====================================================================
  if (!autorise) {
    return <div className="chg"><style>{CSS}</style><p className="note err">{texteAccesRefuse(droits.lire ? DROIT_CHARGER : DROIT_LIRE)}</p></div>;
  }

  const fil = <Breadcrumbs items={[{ label: 'Commandes prêtes', onClick: () => navigate('/commande-prete') }, { label: 'Chargement' }]} />;
  const boiteMessage = (
    <>
      {alertes.length > 0 && (
        <div className="chg-msg err" id="ch-alertes" role="alert">
          {alertes.map((a) => <div key={a.id} className="chg-alerte">✗ {a.txt}</div>)}
          <button type="button" className="btn small" id="ch-vu" onMouseDown={garderFocus} onClick={toutVu}>
            {alertes.length > 1 ? `Vu (${alertes.length})` : 'Vu'}
          </button>
        </div>
      )}
      {message && (
        <div className={`chg-msg ${message.type}`} id="ch-msg" role="status">
          <div>{message.txt}</div>
          {(message.detail || []).map((x) => <div key={x} className="chg-msg-detail">⚠ {x}</div>)}
        </div>
      )}
    </>
  );

  // ----- Étape 1 : camion (v3 : plus de client — il vient de l'encodage) -----
  if (!ch) {
    return (
      <div className="chg" ref={racine}>
        <style>{CSS}</style>
        {fil}
        <h1>🚚 Chargement</h1>
        <p className="note">Scannez la réf. de chargement (BROUT…) pour ouvrir la commande, puis chaque palette / colis qui monte dans le camion — plusieurs clients possibles dans le même départ. Ce qui n'est pas scanné reste « à quai » (le client le voit sur sa page).</p>
        <div className="carte">
          <div className="field">
            <label htmlFor="ch-camion">Camion / transporteur (facultatif)</label>
            <input id="ch-camion" ref={champCamion} type="text" autoComplete="off" value={camion} maxLength={MAX_CAMION} autoFocus
              placeholder="ex. plaque 1-ABC-123, DHL…"
              onChange={(e) => setCamion(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commencer(); } }} />
          </div>
          <button type="button" className="btn primary big" id="ch-commencer" disabled={ouverture} onClick={commencer}>
            {ouverture ? '⏳ Ouverture…' : 'Commencer'}
          </button>
        </div>
        {boiteMessage}
        <Link to="/commande-prete" className="note">← Liste des commandes</Link>
      </div>
    );
  }

  // ----- Étape 3-4 : le départ -----
  const r = resumeDepart(ch);
  const valider = peutValider(ch);
  const dernier = dernierLot(ch);
  const mailsDepart = textesMailsDepart(ch);
  // Ancien écran : départ « pour ce client » ; sinon un départ de tous les clients.
  const titre = ch.client && ch.client.name ? `🚚 ${ch.client.name}` : '🚚 Départ';

  return (
    <div className="chg" ref={racine}>
      <style>{CSS}</style>
      {fil}
      <div className="entete">
        <div>
          <h1>{titre}</h1>
          <span className="note">{`Camion : ${ch.truck || '—'} · départ ouvert ${ch.createdAt ? dateHeure(ch.createdAt) : ''}`}</span>
          {texteDepartReserve(ch) && <span className="note warn" id="ch-reserve">{texteDepartReserve(ch)}</span>}
        </div>
      </div>

      {ouvert ? (
        <div className="carte scan">
          <label htmlFor="ch-scan">Scanner la réf. de chargement, puis l'étiquette de chaque palette / colis qui monte</label>
          <input id="ch-scan" ref={champScan} type="text" autoFocus autoComplete="off" autoCapitalize="characters" spellCheck={false}
            inputMode="text" className="mono" value={scan} placeholder="Réf. BROUT… ou SSCC / étiquette EffiTrace + Entrée"
            onChange={(e) => setScan(e.target.value)} onKeyDown={onEnterScan} />
          {boiteMessage}
          {choix && choix.lid === ch.id && (
            <div className="choix" id="ch-choix" role="group" aria-label="Commandes de cette réf.">
              <b>Plusieurs commandes portent cette réf. : laquelle monte ?</b>
              {choix.items.map((c) => (
                <button type="button" key={c.orderId} className="btn" disabled={!c.chargeable || fermeture} title={c.motif || undefined}
                  onMouseDown={garderFocus} onClick={() => ouvrirChoisie(c)}>
                  {libelleChoix(c)}{c.chargeable ? '' : ` — ${c.motif}`}
                </button>
              ))}
              <button type="button" className="btn small" onMouseDown={garderFocus} onClick={() => { setChoix(null); setMessage((m) => (m && m.choix ? null : m)); focusScan(); }}>Aucune</button>
            </div>
          )}
          <div className="bilan" id="ch-bilan" role="status">
            {`Chargé dans ce départ : ${texteUnites(r.unitesIci)}`}
            {r.totalQuai ? ` · encore à quai : ${texteUnites(r.totalQuai)}` : ' · plus rien à quai'}
            {fermeture ? ' · ⏳ validation du départ…' : enCours > 0 ? ' · ⏳ enregistrement…' : ''}
          </div>
          <div className="actions">
            <button type="button" className="btn" id="ch-annuler" disabled={fermeture || !peutAnnuler(ch)} onMouseDown={garderFocus} onClick={annuler}>
              {dernier && dernier.unites.length > 1 ? `↶ Annuler le dernier « Tout charger » (${texteUnites(dernier.unites.length)})` : '↶ Annuler le dernier scan'}
            </button>
            <button type="button" className={'btn primary' + (valider.ok ? '' : ' inactif')} id="ch-valider" aria-disabled={!valider.ok}
              onMouseDown={garderFocus} onClick={ouvrirRecap}>
              ✓ Valider le départ
            </button>
          </div>
          {dernier && <span className="note" id="ch-dernier">{`Dernier chargé : ${texteLot(dernier)} (${heure(dernier.unites[0].unite.loadedAt)})`}</span>}
          {!valider.ok && valider.msg && <span className="note">{valider.msg}</span>}
          {recap && (
            <div className="recap" id="ch-recap">
              <b>{`Valider le départ${ch.truck ? ` (camion ${ch.truck})` : ''} ?`}</b>
              {texteRecapDepart(ch).map((l, i) => <span key={i} className={l.startsWith('  ') ? 'recap-l indent' : 'recap-l'}>{l.trim()}</span>)}
              <span className="note">Après validation, ce départ ne se modifie plus.</span>
              <div className="actions">
                <button type="button" className="btn primary" id="ch-confirmer" disabled={enCours > 0} onMouseDown={garderFocus} onClick={confirmerDepart}>
                  {enCours > 0 ? '⏳ Enregistrement…' : 'Confirmer le départ'}
                </button>
                <button type="button" className="btn" id="ch-revenir" disabled={fermeture} onMouseDown={garderFocus} onClick={() => { setRecap(false); focusScan(); }}>
                  Revenir au chargement
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="carte">
          <b className="ok">{`✓ Départ validé ${ch.closedAt ? `le ${dateHeure(ch.closedAt)}` : ''} — ${texteUnites(r.unitesIci)} chargée${r.unitesIci > 1 ? 's' : ''}.`}</b>
          {mailsDepart.map((m) => <span key={m.txt} className={'note' + (m.ok ? '' : ' err')} id={mailsDepart.length === 1 ? 'ch-mail' : undefined}>{m.txt}</span>)}
          {mailIncertain(ch) && (
            <button type="button" className="btn small" onClick={() => { const lid = ch.id; enfiler(() => relire(lid)); }}>↻ Recharger l'état du mail</button>
          )}
          {boiteMessage}
          <div className="actions">
            <button type="button" className="btn primary" disabled={enCours > 0} onClick={nouveauDepart}>{enCours > 0 ? '⏳ Nouveau départ' : 'Nouveau départ'}</button>
          </div>
        </div>
      )}

      <div className="cmds">
        {!ch.commandes.length && <p className="note">{ch.client ? 'Aucune commande à quai pour ce client.' : MSG_AUCUNE_COMMANDE}</p>}
        {ch.commandes.map((c) => {
          const rc = resumeCommande(c, ch.id);
          const mail = etatMailCommande(c);
          const chargeable = peutToutCharger(ch, c);
          const conf = ouvert && aConfirmer && aConfirmer.orderId === c.id && confirmationValable(aConfirmer, ch) ? aConfirmer : null;

          return (
            <section className={`cmd ${rc.etat}`} key={c.id}>
              <div className="cmd-head">
                <span className="mono ref">{c.orderRef}</span>
                {c.isTest && <BadgeTest />}
                <span className="cmd-client">{clientDe(c)}</span>
                <span className={`pill ${rc.etat}`}>
                  {rc.etat === 'VIDE' ? 'aucune unité' : rc.etat === 'CHARGEE' ? 'tout est chargé'
                    : `${rc.ici + rc.avant} / ${rc.total} chargée${rc.ici + rc.avant > 1 ? 's' : ''}`}
                </span>
              </div>
              {!mail.ok && <span className="note warn">⚠ {mail.txt}</span>}
              <ul className="unites">
                {(c.unites || []).map((u) => {
                  const e = etatUnite(u, ch.id);
                  return (
                    <li key={u.id} className={`unite ${e}`}>
                      <span className="u-nom"><b>{u.nom}</b> <span className="mono">{u.sscc ? abregeSscc(u.sscc) : 'sans SSCC'}</span></span>
                      <span className={`pill ${e}`}>
                        {e === 'QUAI' ? 'à quai' : e === 'ICI' ? `chargée ${heure(u.loadedAt)}` : `chargée le ${dateHeure(u.loadedAt)} (autre départ)`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {rc.sansEtiquette > 0 && (
                <span className="note warn">{`${rc.sansEtiquette} fiche${rc.sansEtiquette > 1 ? 's' : ''} sans étiquette (SSCC) : ne se charge${rc.sansEtiquette > 1 ? 'nt' : ''} pas — complétez-la${rc.sansEtiquette > 1 ? 's' : ''} sur l'écran de la commande.`}</span>
              )}
              {ouvert && !conf && (
                <button type="button" className="btn" disabled={fermeture || !chargeable || toutEnCours.has(`${ch.id}:${c.id}`)} onMouseDown={garderFocus} onClick={() => demanderToutCharger(c)}>
                  {toutEnCours.has(`${ch.id}:${c.id}`) ? '⏳ Chargement…' : rc.quai ? `Tout charger : ${texteAQuai(rc)}` : rc.sansEtiquette ? 'Rien de chargeable (sans étiquette)' : rc.total ? 'Tout est chargé' : 'Rien à charger'}
                </button>
              )}
              {conf && (
                <div className="recap" role="group" aria-label={`Confirmer le chargement de ${c.orderRef}`}>
                  <b>{texteConfirmerTout(c, conf, user && user.name)}</b>
                  {conf.change && <span className="note warn">La commande a changé depuis votre demande : vérifiez cette phrase avant de confirmer.</span>}
                  <div className="actions">
                    <button type="button" className="btn primary" disabled={fermeture} onMouseDown={garderFocus} onClick={() => toutCharger(c, conf)}>✓ Oui, je confirme</button>
                    <button type="button" className="btn" onMouseDown={garderFocus} onClick={() => { setAConfirmer(null); focusScan(); }}>Non</button>
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// Styles (portée .chg) — mêmes couleurs que l'écran PDA ; au doigt : boutons ≥ 44 px.
const CSS = `
.chg{max-width:720px;margin:0 auto;padding:.75rem;color:#17232a;font-size:16px;line-height:1.45;display:flex;flex-direction:column;gap:12px}
.chg .choix{display:flex;flex-direction:column;gap:8px;padding:10px;border-radius:8px;background:#fbecd3;border:1px solid #e0b25c}
.chg .cmd-client{font-weight:700;color:#334155;overflow-wrap:anywhere}
.chg *{box-sizing:border-box}
.chg h1{font-size:1.25rem;margin:0;overflow-wrap:anywhere}
.chg .note{font-size:.86rem;color:#5b6b73;overflow-wrap:anywhere}
.chg .note.err{color:#a6342b}
.chg .note.warn{color:#965600}
.chg .mono{font-family:"JetBrains Mono",Consolas,monospace}
.chg .carte{background:#f7faf9;border:1px solid #cfd8dc;border-radius:14px;padding:12px;display:flex;flex-direction:column;gap:10px;min-width:0}
.chg .field{display:flex;flex-direction:column;gap:4px;min-width:0}
.chg label{font-size:.8rem;font-weight:700;color:#5b6b73;letter-spacing:.02em}
.chg input[type=text]{font:inherit;font-size:16px;color:#17232a;background:#fff;border:1px solid #cfd8dc;border-radius:8px;padding:8px 10px;width:100%;min-width:0;min-height:44px}
.chg input:disabled{opacity:.6;cursor:not-allowed;background:#e9eef0}
.chg #ch-scan{font-size:1.25rem;min-height:56px;border:2px solid #0f6e74}
.chg input:focus-visible,.chg button:focus-visible{outline:3px solid #0f6e74;outline-offset:2px}
.chg .btn{font:inherit;font-weight:700;border-radius:10px;border:1px solid #cfd8dc;background:#fff;color:#17232a;padding:10px 14px;cursor:pointer;min-height:44px;display:inline-flex;align-items:center;justify-content:center;gap:4px;text-align:center}
.chg .btn.primary{background:#0f6e74;border-color:#0f6e74;color:#fff}
.chg .btn.big{width:100%;padding:14px;font-size:1.05rem}
.chg .btn.small{padding:6px 10px;font-size:.9rem;font-weight:600;min-height:40px}
.chg .btn:disabled,.chg .btn.inactif{opacity:.45;cursor:not-allowed}
.chg .entete{display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap}
.chg .actions{display:grid;grid-template-columns:1fr 1fr;gap:8px}
@media (max-width:420px){.chg .actions{grid-template-columns:1fr}}
.chg .bilan{font-weight:700;font-size:.95rem}
.chg .chg-msg{border-radius:10px;padding:12px 14px;font-weight:800;overflow-wrap:anywhere}
.chg .chg-msg.ok{background:#dcefdc;color:#2a7034;font-size:1.1rem}
.chg .chg-msg.warn{background:#fbecd3;color:#965600;font-size:1.1rem}
.chg .chg-msg.err{background:#f8dedb;color:#a6342b;font-size:1.4rem;border:2px solid #a6342b}
.chg .chg-msg-detail{font-size:.95rem;font-weight:700;margin-top:4px}
.chg .chg-alerte+.chg-alerte{margin-top:6px;padding-top:6px;border-top:1px solid #e9b4ae}
.chg #ch-alertes .btn{margin-top:8px;border-color:#a6342b;color:#a6342b}
.chg .recap{display:flex;flex-direction:column;gap:6px;padding:10px;border-radius:10px;border:2px solid #0f6e74;background:#d7ecec}
.chg .recap-l{font-size:.92rem}
.chg .recap-l.indent{padding-left:14px}
.chg .ok{color:#2a7034}
.chg .cmds{display:flex;flex-direction:column;gap:10px}
.chg .cmd{border:2px solid #cfd8dc;border-radius:12px;background:#fff;padding:10px;display:flex;flex-direction:column;gap:8px;min-width:0}
.chg .cmd.CHARGEE{border-color:#2a7034}
.chg .cmd.PARTIELLE{border-color:#965600}
.chg .cmd-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.chg .cmd-head .ref{font-weight:800;overflow-wrap:anywhere}
.chg .unites{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:4px}
.chg .unite{display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 8px;border-radius:8px;border:1px solid #e2e8f0}
.chg .unite.ICI{background:#dcefdc;border-color:transparent}
.chg .unite.AVANT{background:#f1f5f9;border-color:transparent;color:#5b6b73}
.chg .u-nom{overflow-wrap:anywhere;min-width:0}
.chg .pill{font-size:.8rem;font-weight:700;padding:2px 10px;border-radius:999px;white-space:nowrap}
.chg .pill.QUAI{background:#fbecd3;color:#965600}
.chg .pill.ICI,.chg .pill.CHARGEE{background:#dcefdc;color:#2a7034}
.chg .pill.AVANT{background:#e2e8f0;color:#475569}
.chg .pill.PARTIELLE{background:#dbeafe;color:#1e40af}
.chg .pill.VIDE{background:#f1f5f9;color:#64748b}
`;
